package com.swetdot.swetgram.ui.moderation

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.input.KeyboardCapitalization
import com.swetdot.swetgram.data.moderation.ModerationReport
import com.swetdot.swetgram.data.moderation.ModerationText
import com.swetdot.swetgram.data.moderation.ReportAction
import com.swetdot.swetgram.data.moderation.ReportDecision
import com.swetdot.swetgram.data.moderation.ReportTargetKind
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetCard
import com.swetdot.swetgram.ui.design.SwetDestructiveButton
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetListRowSkeleton
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetTextField
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.discover.discoverTimestamp
import com.swetdot.swetgram.ui.profile.ProfileConfirmDialog
import com.swetdot.swetgram.ui.profile.ProfileControlRow
import com.swetdot.swetgram.ui.profile.ProfileSectionHeader
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.text.resolveOrNull
import com.swetdot.swetgram.ui.text.text
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.StringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.moderation_action_desc_delete_permanent
import swetgram.shared.generated.resources.moderation_confirm_head_block
import swetgram.shared.generated.resources.moderation_confirm_head_delete_account
import swetgram.shared.generated.resources.moderation_confirm_head_delete_comment
import swetgram.shared.generated.resources.moderation_confirm_head_delete_group
import swetgram.shared.generated.resources.moderation_confirm_head_delete_message
import swetgram.shared.generated.resources.moderation_confirm_head_delete_post
import swetgram.shared.generated.resources.moderation_confirm_head_delete_unknown
import swetgram.shared.generated.resources.moderation_confirm_head_none
import swetgram.shared.generated.resources.moderation_confirm_tail_reject
import swetgram.shared.generated.resources.moderation_confirm_tail_resolve
import kotlinx.datetime.TimeZone
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * One report, and the decision -- `adminGetReport` then `adminResolveReport`.
 *
 *
 * THE HONEST PART, WHICH IS MOST OF THIS SCREEN
 *
 * A moderator opens this to answer "is this a violation", and the app cannot show them the thing
 * that was reported. `Report` carries a `target_type`, a bare uuid, a reason, the reporter's
 * optional line of text and a content hash. It has no preview, no title and no author, and no
 * operation in v1.0.0 could supply one -- fetching a reported post through `getDiscoverPost` answers
 * 404 for a private group the moderator has not joined, which is most of the interesting cases. STA
 * §16.1 asks for "view reported user/post/message metadata"; frozen v1.0.0 declares nothing that
 * serves it, and inventing a field on `Report` to close it is exactly what must not happen.
 *
 * So the screen SAYS SO, in a sentence, where the content would be. A blank space there would read
 * as a load that failed, and a moderator would keep pulling to refresh something that is never
 * coming.
 *
 *
 * WHAT THE FORM OFFERS, AND WHAT IT DELIBERATELY DOES NOT
 *
 * A decision (`resolve` / `reject`), a side effect, and a note. [availableActions] narrows the side
 * effects against the report -- `delete_content` only where there is content, `block_user` only
 * where the report names an account -- and its documentation carries the argument, including the
 * under-offer that is deliberate.
 *
 * The NOTE is collected and labelled truthfully: it is stored in `reports.resolution_note` and
 * **nothing in v1.0.0 can read it back**, because `Report` has no property for it. Saying that
 * under the field is better than letting a moderator write a paragraph they expect a colleague to
 * find.
 *
 *
 * CONFIRMATION IS NOT OPTIONAL HERE. SDS §11.1 requires a confirmation dialog for destructive
 * actions, and every path out of this form is one: both decisions are terminal (no operation
 * reopens a report), and two of the three side effects destroy something that cannot be brought
 * back. The dialog names the actual consequence rather than asking "are you sure?", which is
 * §18.2's rule and `ProfileConfirmDialog`'s reason for taking its copy from the caller.
 */
@Composable
fun ModerationReportScreen(
    state: ModerationReportUiState,
    onBack: () -> Unit,
    onRefresh: () -> Unit,
    onResolve: (ReportDecision, ReportAction, String?) -> Unit,
    onDismissError: () -> Unit,
    modifier: Modifier = Modifier,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    val now = remember { Clock.System.now() }
    val report = state.report

    // Saved, not merely remembered. Turning the phone here used to discard a decision, a chosen
    // side effect and a written note -- and the note is the one thing on this screen that costs
    // real effort to type. The savers are `ReportFlow.kt`'s; they store the enum's `name`, which
    // is a String and so is portable to every Compose Multiplatform target unchanged.
    var decision by rememberSaveable(stateSaver = optionalEnumSaver(ReportDecision.entries)) {
        mutableStateOf<ReportDecision?>(null)
    }
    var action by rememberSaveable(
        stateSaver = requiredEnumSaver(ReportAction.entries, ReportAction.NONE),
    ) {
        mutableStateOf(ReportAction.NONE)
    }
    var note by rememberSaveable { mutableStateOf("") }
    var confirming by rememberSaveable { mutableStateOf(false) }

    // A RESTORED side effect is clamped to what this report actually offers. Rotation reloads the
    // same report so the two normally agree -- but the value that gets submitted must never be one
    // the radio group is not showing, and the ones that can go missing here suspend an account and
    // delete content. [availableActions] narrows the list against the report; this obeys it.
    val chosenAction = if (action in state.actions) action else ReportAction.NONE

    Column(modifier.fillMaxSize()) {
        // TODO(localization): every user-facing string in this file moves into resources (EN + RU).
        // The confirmation heads and tails below ARE resourced already (pass 1's six
        // gender-agreeing sentences); the chrome around them is blocked on the `moderation_` keys
        // living in the shared `values/strings.xml`. `ReportFlow.kt` carries the full note.
        SwetTopAppBar(title = "Report", onBack = onBack)

        if (state.error != null) {
            SwetErrorBanner(
                message = state.error,
                onRetry = if (state.forbidden || state.missing) null else onRefresh,
                onDismiss = onDismissError,
            )
        }

        when {
            state.forbidden -> SwetEmptyState(
                title = "Not available.",
                body = "This account does not have moderator access.",
                actionText = "Back",
                onAction = onBack,
            )

            state.missing -> SwetEmptyState(
                title = "That report is gone.",
                body = "It may have been removed with the account that filed it.",
                actionText = "Back",
                onAction = onBack,
            )

            report == null && !state.loadedOnce -> Column(Modifier.fillMaxSize()) {
                repeat(SKELETON_ROWS) { SwetListRowSkeleton() }
            }

            report == null -> SwetEmptyState(
                title = "Report unavailable.",
                body = "We could not load it.",
                actionText = "Try again",
                onAction = onRefresh,
            )

            else -> Column(
                modifier = Modifier
                    .fillMaxSize()
                    // A pushed detail route, so nothing under it carries the system-bar inset.
                    // Before `verticalScroll`, so the inset pads the scroller rather than the
                    // content inside it -- `PrivacyScreen` records why.
                    .navigationBarsPadding()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = Space.screenMargin),
            ) {
                Spacer(Modifier.height(Space.two))

                ReportFacts(report = report, now = now, zone = zone)

                Spacer(Modifier.height(Space.two))

                if (state.isDecided) {
                    DecidedNotice(report)
                } else {
                    ResolveForm(
                        state = state,
                        decision = decision,
                        action = chosenAction,
                        note = note,
                        onDecision = { decision = it },
                        onAction = { action = it },
                        onNote = { note = it },
                        onSubmit = { confirming = true },
                    )
                }

                Spacer(Modifier.height(Space.six))
            }
        }
    }

    val chosen = decision
    if (confirming && chosen != null && report != null) {
        ProfileConfirmDialog(
            title = resolveConfirmTitle(chosen),
            body = resolveConfirmBody(chosen, chosenAction, report).resolve(),
            confirmText = "Confirm",
            // Red only when something is destroyed. A `reject` with no action destroys nothing and
            // painting it `danger` would put the warning colour on the harmless decision.
            destructive = chosenAction != ReportAction.NONE,
            onConfirm = {
                confirming = false
                onResolve(chosen, chosenAction, note)
            },
            onDismiss = { confirming = false },
            busy = state.busy,
        )
    }
}

private const val SKELETON_ROWS = 5

// ---------------------------------------------------------------------------------------------
// What is known
// ---------------------------------------------------------------------------------------------

/**
 * Everything the contract gives a moderator about a report, and the sentence about what it does not.
 */
@Composable
private fun ReportFacts(report: ModerationReport, now: Instant, zone: TimeZone) {
    val colors = SwetgramThemeTokens.colors

    SwetCard(showBorder = true, modifier = Modifier.fillMaxWidth()) {
        Text(
            text = "${reportTargetLabel(report)} - ${reportReasonLabel(report)}",
            style = MaterialTheme.typography.titleLarge,
            color = colors.textPrimary,
        )
        Spacer(Modifier.height(Space.half))
        Text(
            text = reportStatusLabel(report.status),
            style = MaterialTheme.typography.labelMedium,
            // Weight rather than hue, and the same rule the queue applies -- see the note there.
            color = if (report.status.isOpen) colors.textPrimary else colors.textSecondary,
        )

        report.createdAt?.let {
            Spacer(Modifier.height(Space.half))
            Text(
                // `.resolve()` IS LOAD-BEARING, and its absence did not fail the build. Since
                // `discoverTimestamp` began returning a [UiText], a string template around it
                // compiled happily and printed the data class -- "Filed Res(id=..., args=[...])".
                // A type error everywhere else; a silent wrong screen inside a template.
                text = "Filed ${discoverTimestamp(it, now, zone).resolve()}",
                style = MaterialTheme.typography.labelSmall,
                color = colors.textSecondary,
            )
        }

        Spacer(Modifier.height(Space.two))
        FactRow(label = "Target id", value = report.targetId)
        // The hash is not the content and cannot be turned back into it. It answers two questions
        // that survive a hard delete: is the thing I am deleting the thing that was reported, and do
        // two reports name the same text. STA §16.3, STA §31.4.
        report.contentHash?.let { FactRow(label = "Content hash", value = it) }
        report.reporterId?.let { FactRow(label = "Reported by", value = it) }
        report.assignedTo?.let { FactRow(label = "Decided by", value = it) }

        val details = report.details?.trim().orEmpty()
        if (details.isNotEmpty()) {
            Spacer(Modifier.height(Space.two))
            ProfileSectionHeader("What the reporter wrote")
            Text(
                text = details,
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textPrimary,
                modifier = Modifier.padding(horizontal = Space.two),
            )
        }

        Spacer(Modifier.height(Space.two))
        Text(
            // Stated, not left blank. See the class header.
            text = "The reported ${reportTargetLabel(report).lowercase()} is not shown here. This " +
                "version of the API has no way to fetch it for a moderator, so the id above is the " +
                "handle you have.",
            style = MaterialTheme.typography.bodySmall,
            color = colors.textSecondary,
        )
    }
}

/**
 * A label and a uuid.
 *
 * The value is NOT truncated and NOT ellipsised. Every one of these is an identifier somebody will
 * copy into a query, and half a uuid is worse than none because it looks usable.
 */
@Composable
private fun FactRow(label: String, value: String) {
    val colors = SwetgramThemeTokens.colors
    Column(Modifier.fillMaxWidth().padding(vertical = Space.half)) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = colors.textSecondary,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodySmall,
            color = colors.textPrimary,
        )
    }
}

/** A decided report is read-only: no operation in v1.0.0 reopens one. */
@Composable
private fun DecidedNotice(report: ModerationReport) {
    val colors = SwetgramThemeTokens.colors
    Text(
        text = "This report was already decided as \"${reportStatusLabel(report.status)}\". " +
            "Nothing here can reopen it.",
        style = MaterialTheme.typography.bodyMedium,
        color = colors.textSecondary,
    )
}

// ---------------------------------------------------------------------------------------------
// The decision
// ---------------------------------------------------------------------------------------------

@Composable
private fun ResolveForm(
    state: ModerationReportUiState,
    decision: ReportDecision?,
    action: ReportAction,
    note: String,
    onDecision: (ReportDecision) -> Unit,
    onAction: (ReportAction) -> Unit,
    onNote: (String) -> Unit,
    onSubmit: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    ProfileSectionHeader("Decision")
    Column(Modifier.selectableGroup()) {
        ReportDecision.entries.forEach { value ->
            val isSelected = value == decision
            ProfileControlRow(
                title = decisionLabel(value),
                supporting = decisionDescription(value),
                modifier = Modifier.selectable(
                    selected = isSelected,
                    role = Role.RadioButton,
                    onClick = { onDecision(value) },
                ),
            ) {
                RadioButton(selected = isSelected, onClick = null)
            }
        }
    }

    Spacer(Modifier.height(Space.two))

    ProfileSectionHeader("And then")
    Column(Modifier.selectableGroup()) {
        state.actions.forEach { value ->
            val isSelected = value == action
            ProfileControlRow(
                title = actionLabel(value, state.report?.targetKind),
                supporting = actionDescription(value, state.report?.targetKind).resolveOrNull(),
                modifier = Modifier.selectable(
                    selected = isSelected,
                    role = Role.RadioButton,
                    onClick = { onAction(value) },
                ),
            ) {
                RadioButton(selected = isSelected, onClick = null)
            }
        }
    }

    Spacer(Modifier.height(Space.two))

    SwetTextField(
        value = note,
        onValueChange = onNote,
        placeholder = "Why (optional)",
        singleLine = false,
        keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
        errorText = if (note.trim().length > ModerationText.NOTE_MAX) {
            "At most ${ModerationText.NOTE_MAX} characters"
        } else {
            null
        },
    )
    Spacer(Modifier.height(Space.half))
    Text(
        // Truthful rather than encouraging. The field is write-only under v1.0.0.
        text = "Stored with the report. No screen in this app can show it again.",
        style = MaterialTheme.typography.bodySmall,
        color = colors.textSecondary,
    )

    Spacer(Modifier.height(Space.three))

    // A single control, whose colour follows the side effect rather than the decision: the thing
    // worth warning about is the deletion or the suspension, not the closing of a ticket.
    if (action == ReportAction.NONE) {
        SwetPrimaryButton(
            text = "Close this report",
            onClick = onSubmit,
            enabled = decision != null && !state.busy,
            loading = state.busy,
        )
    } else {
        SwetDestructiveButton(
            text = "Close this report",
            onClick = onSubmit,
            enabled = decision != null && !state.busy,
        )
    }
}

// ---------------------------------------------------------------------------------------------
// Confirmation copy -- pure, so it can be exercised without a renderer
// ---------------------------------------------------------------------------------------------

internal fun resolveConfirmTitle(decision: ReportDecision): String = when (decision) {
    ReportDecision.RESOLVE -> "Close this as a violation?"
    ReportDecision.REJECT -> "Close this as not a violation?"
}

/**
 * The consequence, named -- SDS §18.2 wants the sentence to say what will happen, and
 * `ProfileConfirmDialog` takes its copy from the caller for exactly this reason.
 *
 * Two facts go in every version of it: the decision cannot be undone (no operation in v1.0.0
 * reopens a report), and the side effect, spelled out where there is one.
 */
internal fun resolveConfirmBody(
    decision: ReportDecision,
    action: ReportAction,
    report: ModerationReport,
): UiText {
    val head = when (action) {
        ReportAction.NONE -> Res.string.moderation_confirm_head_none.text()
        // The consequence sentence is NESTED rather than repeated, so the dialog and the option row
        // under the radio button cannot drift apart. `actionDescription` is never null for
        // DELETE_CONTENT, and an empty fallback would silently swallow a future null rather than
        // failing where it could be seen.
        //
        // The SENTENCE is chosen by the kind; nothing is substituted into a noun slot. See
        // [deleteConfirmHead].
        ReportAction.DELETE_CONTENT -> deleteConfirmHead(report.targetKind).text(
            actionDescription(action, report.targetKind)
                ?: Res.string.moderation_action_desc_delete_permanent.text(),
        )
        ReportAction.BLOCK_USER -> Res.string.moderation_confirm_head_block.text()
    }
    val tail = when (decision) {
        ReportDecision.RESOLVE -> Res.string.moderation_confirm_tail_resolve.text()
        ReportDecision.REJECT -> Res.string.moderation_confirm_tail_reject.text()
    }
    return UiText.Joined(listOf(head, tail), " ")
}

/**
 * The delete sentence for one kind of target: A WHOLE SENTENCE EACH, never a noun in a slot.
 *
 * WHAT THIS REPLACED, because the shape of the bug is the argument for the shape of the fix. There
 * was one key, `moderation_confirm_head_delete`, reading "The %1$s is deleted. %2$s" in English and
 * "Объект (%1$s) будет удалён. %2$s" in Russian, and its first argument was
 * `reportTargetLabel(report).lowercase()` -- which returns HARDCODED ENGLISH. So a Russian moderator
 * about to delete a Discover post was asked to confirm "Объект (post) будет удалён."
 *
 * THE PARENTHESIS WAS THE TELL, and it is why translating `reportTargetLabel` would not have fixed
 * this. A Russian past passive participle agrees in GENDER with its subject: запись будет удален*а*,
 * сообщение будет удален*о*, комментарий будет удален. A sentence with a hole in it cannot agree
 * with a noun it does not know, so the bracket was not a stylistic choice; it was the only way to
 * write that sentence at all, and it existed because the sentence was impossible. The `.lowercase()`
 * went with it: it was only ever patching English capitalisation into the middle of a clause.
 *
 * [reportTargetLabel] SURVIVES and is deliberately untouched. It still has three English-only
 * callers -- the report card title, the "not shown here" sentence below it, and the queue row -- all
 * of which are un-localised prose that a later pass moves wholesale. It is a NOUN, which is the
 * right shape for a heading and the wrong shape for a predicate.
 *
 * PERSON AND GROUP ARE NOT REACHABLE from the form: [availableActions] offers `delete_content` only
 * where `namesContent` is true, which is a message, a post or a comment. Their sentences exist so
 * this is total over [ReportTargetKind] rather than falling into a default arm that would quietly
 * absorb a sixth kind. If `delete_content` is ever offered on a `user` or a `group` target, both
 * sentences owe a live probe first: v1.0.0 does not say what the server would delete.
 *
 * NULL is a `target_type` this build does not know, which the queue draws rather than hides. Its
 * sentence names no noun because there is no noun to name.
 */
internal fun deleteConfirmHead(kind: ReportTargetKind?): StringResource = when (kind) {
    ReportTargetKind.DISCOVER_POST -> Res.string.moderation_confirm_head_delete_post
    ReportTargetKind.DISCOVER_COMMENT -> Res.string.moderation_confirm_head_delete_comment
    ReportTargetKind.MESSAGE -> Res.string.moderation_confirm_head_delete_message
    ReportTargetKind.PERSON -> Res.string.moderation_confirm_head_delete_account
    ReportTargetKind.GROUP -> Res.string.moderation_confirm_head_delete_group
    ReportTargetKind.PICSO_POST -> Res.string.moderation_confirm_head_delete_post
    null -> Res.string.moderation_confirm_head_delete_unknown
}
